import type { ComponentProps } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import ModalBase from 'LuxKit/Modal/ModalBase'
import type { EnabledFilters } from 'components/Search/type'
import HotelSearchPageFilterPane, {
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { OfferListSortOption } from 'constants/offerListFilters'
import { queryKeyFilters } from 'constants/url'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import { getAppliedFilterCount } from 'components/Search/utils'
import Group from 'components/utils/Group'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { STORE_MAP_PAGE_ZOOM } from 'lib/search/constants'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useIntl } from 'react-intl'
import noop from 'lib/function/noop'

const ZoomedContent = styled.div<{ $applyZoom?: boolean }>`
  ${props => props.$applyZoom && `
    zoom: ${STORE_MAP_PAGE_ZOOM};
  `}
`

interface MappedStateProps {
  currentHash: string
}

type InheritedHotelSearchPageFilterPaneProps = Pick<ComponentProps<typeof HotelSearchPageFilterPane>, 'onFilterChange' | 'overrideAvailableFilters' | 'doNotScrollToTopOfSelection'>

interface Props extends InheritedHotelSearchPageFilterPaneProps {
  filters: App.OfferListFilters
  search: string
  region?: string
  enabledFilters: EnabledFilters
  open?: boolean
  onClose?: () => void
  onViewOffers?: () => void
  fetching?: boolean
  sortOptions?: Array<OfferListSortOption>
  hideAddDestinationToggle?: boolean
  excludeOfferTypesFromCount?: boolean
  applyZoom?: boolean
  showRoomsAndAmenitiesAtTop?: boolean
  enableSearchVerticalTabsExperiment?: boolean
}

function OfferListFilterSidebar({
  currentHash,
  open,
  onClose,
  onViewOffers = noop,
  doNotScrollToTopOfSelection,
  fetching,
  sortOptions,
  filters,
  overrideAvailableFilters,
  search,
  enabledFilters,
  onFilterChange,
  hideAddDestinationToggle = false,
  excludeOfferTypesFromCount = false,
  applyZoom = false,
  showRoomsAndAmenitiesAtTop = false,
  enableSearchVerticalTabsExperiment = false,
}: Props & MappedStateProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const viewingOffers = useRef(false)

  const handleViewOffers = useCallback(() => {
    viewingOffers.current = true
    onClose?.()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    if (viewingOffers.current) {
      viewingOffers.current = false
      onViewOffers()
    }
  }, [onViewOffers])

  const resetBtnText = useMemo(() => {
    const filterCount = getAppliedFilterCount(filters, enabledFilters, excludeOfferTypesFromCount)
    const resetAllWithCount = intl.formatMessage({
      id: 'I8Db5Csyd9TM3y8NnnSP',
      defaultMessage: 'Reset all ({filterCount})',
      description: 'Sidebar reset button text showing the number of active filters',
    }, { filterCount })
    const resetAll = intl.formatMessage({
      id: 'N-LAxpQDDA-0Z0fpx3c7',
      defaultMessage: 'Reset all',
      description: 'Sidebar reset button text when no active filters in the offer listing section',
    })
    return filterCount > 0 ? resetAllWithCount : resetAll
  }, [filters, enabledFilters, excludeOfferTypesFromCount, intl])

  const onReset = useCallback(() => {
    const filtersToDelete = [...queryKeyFilters]
    const newSearch = deleteSearchParams(
      search,
      ...filtersToDelete,
    )
    dispatch(replace({ search: newSearch, hash: currentHash }))
  }, [currentHash, dispatch, search])

  return (
    <ModalBase
      mode="drawer"
      isOpen={open}
      onClose={onClose}
      onAfterClose={handleAfterClose}
    >
      <ModalBody>
        <ModalContent>
          <ZoomedContent $applyZoom={applyZoom}>
            <HotelSearchPageFilterPane
              doNotScrollToTopOfSelection={doNotScrollToTopOfSelection}
              fetching={fetching}
              filters={filters}
              overrideAvailableFilters={overrideAvailableFilters}
              mapViewURL=""
              search={search}
              enabledFilters={enabledFilters}
              sortOptions={sortOptions}
              onClose={onClose}
              onFilterChange={onFilterChange}
              hideAddDestinationToggle={hideAddDestinationToggle}
              excludeOfferTypesFromCount={excludeOfferTypesFromCount}
              applyZoomCompensation={applyZoom}
              showRoomsAndAmenitiesAtTop={showRoomsAndAmenitiesAtTop}
              enableSearchVerticalTabsExperiment={enableSearchVerticalTabsExperiment}
            />
          </ZoomedContent>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ZoomedContent $applyZoom={applyZoom}>
          <Group direction="horizontal" horizontalAlign="stretch">
            <CSSBreakpoint max="mobile">
              <TextButton kind="tertiary" size="large" onClick={onReset}>
                {resetBtnText}
              </TextButton>
            </CSSBreakpoint>
            <TextButton kind="primary" size="large" onClick={handleViewOffers}>
              {intl.formatMessage({
                id: '8PCzNzihLvniTq7jsHCz',
                defaultMessage: 'View Offers',
                description: 'CTA button in filter sidebar to apply filters and view offers',
              })}
            </TextButton>
          </Group>
        </ZoomedContent>
      </ModalFooter>
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    currentHash: state.router.location.hash,
  }
}

export default connect(mapStateToProps)(OfferListFilterSidebar)
